// src/components/frontpage/ContactList.jsx
// Reusable compact contact list for the Locality sidebar.
// Used for both OfficialContacts and UtilityContacts sections.
// Each row: icon + name. Click opens a ContactCard popover.

import React from 'react';
import ContactCard from './ContactCard';
import { SectionTitle, DirectoryRow, DIR_FONT, OpenEntryFrame, OPEN_ENTRY_FRAME_INSET } from './DirectorySection';
import { useTranslation } from '../../i18n/useTranslation';
import { tokens } from '../../styles/designTokens';
import { useExpandInPlace } from '../../hooks/useExpandInPlace';

const ContactList = ({ title, contacts = [], compact = false, drillStyle = 'popout', openListingId, onToggleListing, onSuggestCorrection, isOpen = true, onToggleSection = null }) => {
  const { t } = useTranslation('frontpage');
  // 'accordion' renders the contact detail inline below the row (no absolute popover, no
  // outside-click dismiss); 'popout' keeps the original absolute ContactCard.
  const useAccordion = drillStyle === 'accordion';

  // Open-entry card: controlled by the parent (DirectoryColumn shares one id across every
  // directory section) or local state when those props are absent (SidebarLocality popout).
  // See src/hooks/useExpandInPlace.js.
  const listingGroup = useExpandInPlace({ openId: openListingId, onToggle: onToggleListing });
  const openContactId = listingGroup.openId;
  const handleToggle = listingGroup.toggle;

  const handleClose = () => handleToggle(null);

  return (
    <div style={{ marginBottom: '8px', position: 'relative' }}>
      {/* Section title (shared primitive — theme blue, one definition). onClick makes it the
          concertina's toggle (Directory column shell brief, Scope 3); omitted, it's the original
          non-interactive heading. */}
      <SectionTitle title={title} onClick={onToggleSection} />

      {isOpen && (
      <>
      {/* Honest empty state — no fabricated contacts, no dead blank either. */}
      {contacts.length === 0 && (
        <div style={{ fontFamily: DIR_FONT, fontSize: '12px', fontStyle: 'italic', color: tokens.colors.disabledText }}>
          {t('no_official_contacts')}
        </div>
      )}

      {/* Contact rows — shared DirectoryRow. Bold only when the contact links to a
          verified group (groupId is verified-only). */}
      {contacts.map(contact => {
        const isEntryOpen = openContactId === contact.id;
        const row = (
          <DirectoryRow
            icon={contact.icon}
            label={contact.name}
            isOpen={isEntryOpen}
            isLinked={!!contact.groupId}
            onClick={() => handleToggle(contact.id)}
            compact={compact}
            bullet={true}
            areaRow={contact}
          />
        );
        // ContactCard — inline accordion detail or absolute popover per drillStyle
        const detail = isEntryOpen && (
          <ContactCard
            contact={contact}
            onClose={handleClose}
            inline={useAccordion}
            onSuggestCorrection={onSuggestCorrection}
          />
        );
        return (
          <div key={contact.id} style={{ position: 'relative' }}>
            {useAccordion ? (
              /* Border-encloses-name correction, 16 Aug: OpenEntryFrame wraps the row AND its
                 detail together, so the open-entry border frames the name, not just the detail
                 beneath it. Popout mode (below) is untouched — ContactCard's own popout branch
                 already shows the name inside its own bordered card.
                 Border-position correction, same day: unlike DirectoryNav/UtilityCategoryList's
                 accordion sites (nested inside AccordionBranch's own 28px+14px indent),
                 Official Contacts entries sit directly in the section body, which has no spare
                 left padding before an ancestor's overflow:hidden — OpenEntryFrame's
                 compensating negative margin would bleed past that edge and get the border
                 clipped. This wrapper reserves exactly the room OpenEntryFrame needs, applied
                 whether or not this entry is open so open/closed rows stay aligned with each
                 other (not just internally consistent per entry). */
              <div style={{ paddingLeft: `${OPEN_ENTRY_FRAME_INSET}px` }}>
                <OpenEntryFrame isOpen={isEntryOpen}>
                  {row}
                  {detail}
                </OpenEntryFrame>
              </div>
            ) : (
              <>
                {row}
                {detail}
              </>
            )}
          </div>
        );
      })}
      </>
      )}
    </div>
  );
};

export default ContactList;
